// Lot 593 — stories : image servie à la taille de l'écran (variante _w1280 déjà générée) + préchargement de la story
// SUIVANTE (et de la 1re du groupe suivant) pendant qu'on regarde la story en cours. Meilleur effort : rien n'est
// bloquant, une URL n'est préchargée qu'une fois par session, et l'« Économiseur de données » coupe le préchargement.
import { resolveMediaUrl } from "@/components/FileUpload";
import { imageVariant, IMAGE_VARIANT_WIDTHS } from "@/lib/mediaUrl";
import { isHlsSrc, posterFor } from "@/lib/videoSource";
import { warmHlsOnce } from "@/lib/reelsWarmup";
import { prefetchAllowed, heavyPrefetchAllowed } from "@/lib/prefetchPolicy"; // lot 678 — politique unique

const MP4_PREFETCH_MS = 12000;
const done = new Set();

// Largeur CSS de la scène : plein écran sur téléphone, colonne ≈ 420 px sur PC (la variante est choisie sur le GRAND côté).
const stageWidth = () => (typeof window === "undefined" ? 1080 : Math.min(window.innerWidth || 1080, 1080));

export const storyImageSrc = (story) => {
  if (!story?.media_url) return "";
  // Lot 679 — une story est plein écran (portrait 9:16) : son plus grand côté est la HAUTEUR ; on garde la règle du
  // lot 593 (1280 px, 480 en économiseur) avec la liste d'origine — jamais 864 (floue) ni l'original 1920 (2× plus lourd).
  return imageVariant(story.media_url, stageWidth(), { widths: IMAGE_VARIANT_WIDTHS }) || resolveMediaUrl(story.media_url);
};

const warmImage = (url) => {
  if (!url || done.has(url)) return;
  done.add(url);
  const img = new Image();
  img.decoding = "async";
  img.src = url;
  if (typeof img.decode === "function") img.decode().catch(() => {});
};

// MP4 brut (vidéo pas encore transcodée) : un <video> détaché charge l'en-tête + les premières secondes dans le cache
// média du navigateur, puis est libéré (src vidé) — aucun décodeur n'est pris tant qu'on ne lit pas.
const warmMp4 = (src) => {
  if (typeof document === "undefined" || done.has(src)) return;
  done.add(src);
  try {
    const v = document.createElement("video");
    v.muted = true; v.preload = "auto"; v.playsInline = true;
    const release = () => { v.removeAttribute("src"); try { v.load(); } catch { /* noop */ } };
    const timer = setTimeout(release, MP4_PREFETCH_MS);
    v.addEventListener("canplaythrough", () => { clearTimeout(timer); release(); }, { once: true });
    v.addEventListener("error", () => { clearTimeout(timer); release(); }, { once: true });
    v.src = src;
  } catch { /* noop */ }
};

export const prefetchStory = (story) => {
  // Lot 678 — garde-fou admin, lien 2G, économiseur de données → aucun préchargement.
  if (!story || !prefetchAllowed()) return;
  if (story.media_type === "image") { warmImage(storyImageSrc(story)); return; }
  if (story.media_type === "video") {
    const src = resolveMediaUrl(story.hls_url || story.media_url || "");
    if (!src) return;
    warmImage(posterFor(src, story.poster_url));
    if (isHlsSrc(src)) warmHlsOnce(src);
    // MP4 brut : <video> détaché → évité sur un téléphone à faible mémoire (l'image d'attente suffit).
    else if (heavyPrefetchAllowed()) warmMp4(src);
    return;
  }
  if (story.media_type === "text" && typeof story.bg === "string" && story.bg.startsWith("/")) warmImage(resolveMediaUrl(story.bg));
};

// Story suivante dans le même groupe, sinon la 1re du groupe suivant ; + la 1re du groupe suivant quand on est au
// milieu d'un groupe (le membre peut sauter de groupe d'un tap).
export const upcomingStories = (groups, gIdx, sIdx) => {
  const out = [];
  const g = groups?.[gIdx];
  const nextGroupFirst = groups?.[gIdx + 1]?.stories?.[0];
  if (g?.stories?.[sIdx + 1]) out.push(g.stories[sIdx + 1]);
  if (nextGroupFirst && !out.includes(nextGroupFirst)) out.push(nextGroupFirst);
  return out;
};

// Appelé par la visionneuse à chaque changement de story : léger délai pour laisser la story en cours démarrer d'abord.
export const schedulePrefetch = (stories, delayMs = 400) => {
  if (!stories?.length || !prefetchAllowed()) return () => {}; // lots 671/678 — garde-fou admin + réseau
  const timer = setTimeout(() => stories.forEach(prefetchStory), delayMs);
  return () => clearTimeout(timer);
};
